/**
 * <PaywallFlow /> — unified 3-step paywall + onboarding stepper.
 *
 * Used in three places:
 *  1. Pre-trial cap exceeded → `mode="full"` (plan picker + info + confirm).
 *  2. /settings/billing "Velja áskrift" button → `mode="full"`.
 *  3. First-time signup onboarding → `mode="info-only"` (skip plan picker;
 *     stay in pre-trial until the user hits the cap).
 *
 * The flow ALWAYS pre-fills company info from the kennitala lookup (ja.is →
 * RSK scraper fallback) and asks the user to confirm/edit BEFORE saving. We
 * never push data to Konto silently; every required field is reviewed.
 *
 * iPhone-first: full-screen on mobile, centered modal ≥ md.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { formatISK } from '@/utils/formatISK';
import { billingKontoAPI, companyAPI } from '@/services/api';
import { useI18n } from '@/contexts/I18nContext';
import { usePlan } from '@/contexts/PlanContext';
import { useAuth } from '@/contexts/AuthContext';
import { firstInvoiceLine, planTagline, reopenNotice } from '@/utils/billingCopy';
import { onlyDigits, companyKennitalaError } from '@/utils/kennitala';
import {
  X, Check, Loader2, ArrowRight, ArrowLeft, Sparkles, User, Users, Star, Briefcase,
  Building2, MapPin, Hash, ShieldCheck, AlertCircle,
} from 'lucide-react';

const T = {
  is: {
    titleFull: 'Veldu pakka',
    titleInfo: 'Staðfestu upplýsingar fyrirtækis',
    subtitleFull: 'Allt á einum skjá. Engin falin gjöld.',
    subtitleInfo: 'Við notum þetta til að senda mánaðarlega reikninga rétt.',
    close: 'Loka',
    back: 'Til baka',
    next: 'Áfram',
    perMonth: 'kr / mán',
    inclVat: 'Heildarverð m. VSK',
    noVat: 'Heildarverð · engin viðbótargjöld',
    maxUsers: 'notendur',
    selected: 'Valið',
    pickPlan: 'Velja',
    kennitalaLabel: 'Kennitala fyrirtækis',
    kennitalaHelp: 'Við sækjum nafn og heimilisfang sjálfkrafa.',
    lookupBtn: 'Sækja upplýsingar',
    lookupAgainBtn: 'Sækja aftur',
    looking: 'Leita…',
    foundFromJa: 'Sótt frá Þjóðskrá (ja.is)',
    foundFromRsk: 'Sótt frá Skattinum (RSK)',
    notFound: 'Engar upplýsingar fundust, sláðu inn handvirkt',
    invalidKt: 'Kennitala verður að vera 10 tölustafir.',
    nameLabel: 'Nafn fyrirtækis',
    addressLabel: 'Heimilisfang',
    zipLabel: 'Póstnúmer',
    cityLabel: 'Staður',
    emailLabel: 'Netfang (fyrir reikninga)',
    confirmTitle: 'Þetta munt þú fá',
    confirmPlan: 'Áskrift',
    confirmAmount: 'Mánaðarlegt gjald',
    confirmInvoiceTo: 'Reikningur sendur á',
    confirmNote: 'Þú getur skipt um pakka eða sagt upp hvenær sem er. Krafa fer í heimabanka mánaðarlega.',
    activate: 'Velja pakka',
    activating: 'Virkja…',
    saveAndContinue: 'Vista og halda áfram',
    errorMissingFields: 'Vinsamlegast fylltu út öll reit',
    errorInvalidEmail: 'Ógilt netfang',
    successFull: 'Pakki valinn.',
    successInfo: 'Upplýsingar vistaðar',
    planNames: { solo: 'Einstaklingur', team: 'Teymið', business: 'Stærri fyrirtæki' },
  },
  en: {
    titleFull: 'Choose your subscription',
    titleInfo: 'Confirm your company info',
    subtitleFull: 'Everything on one screen. No hidden fees.',
    subtitleInfo: 'We use this to send monthly invoices correctly.',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    perMonth: 'kr / mo',
    inclVat: 'Total incl. VAT',
    noVat: 'Total · no extra fees',
    maxUsers: 'users',
    selected: 'Selected',
    pickPlan: 'Choose',
    kennitalaLabel: 'Company kennitala',
    kennitalaHelp: 'We auto-fetch name and address.',
    lookupBtn: 'Fetch info',
    lookupAgainBtn: 'Fetch again',
    looking: 'Searching…',
    foundFromJa: 'Found via Þjóðskrá (ja.is)',
    foundFromRsk: 'Found via RSK',
    notFound: 'Nothing found — please enter manually',
    invalidKt: 'Kennitala must be 10 digits.',
    nameLabel: 'Company name',
    addressLabel: 'Street address',
    zipLabel: 'Postal code',
    cityLabel: 'City',
    emailLabel: 'Email (for invoices)',
    confirmTitle: 'Here\'s what you\'ll get',
    confirmPlan: 'Subscription',
    confirmAmount: 'Monthly fee',
    confirmInvoiceTo: 'Invoice sent to',
    confirmNote: 'You can change plan or cancel anytime. Claim is delivered to your home bank monthly.',
    activate: 'Choose plan',
    activating: 'Activating…',
    saveAndContinue: 'Save and continue',
    errorMissingFields: 'Please fill out all fields',
    errorInvalidEmail: 'Invalid email',
    successFull: 'Plan selected.',
    successInfo: 'Info saved',
    planNames: { solo: 'Solo', team: 'Team', business: 'Business' },
  },
};

// One icon per tier so the trio is visually distinct:
//   Sóló  → User (single person silhouette)
//   Teymið → Users (multiple people silhouette)
//   Stærri fyrirtæki → Building2 (the most "company-like" icon, leaving Star
//   free for a future "Premium / Áberandi" upsell row)
const PLAN_ICONS = { solo: User, team: Users, business: Building2 };
// `Intl.NumberFormat('is-IS')` silently falls back to comma grouping when ICU
// lacks the locale, printing "7,990 kr". Use the app-wide de-DE formatter,
// which always gives Icelandic dot grouping: 7.990.

export const PaywallFlow = ({
  open,
  onClose,
  onSuccess,
  mode = 'full',           // 'full' = plan+info+confirm; 'info-only' = info+save
  initialPlan = null,      // pre-select a plan if linked from a CTA
}) => {
  const { lang } = useI18n();
  const planCtx = usePlan();
  const { user: authUser } = useAuth();
  const t = T[lang] || T.en;

  // iter359: one screen. The three-step stepper made the user tap Áfram
  // twice before anything happened, and every step was something we already
  // knew. Everything is on one scrollable screen with ONE button now.
  const [plans, setPlans] = useState([]);
  // Default to false (no VAT) — Rektra is under the 2M kr threshold today.
  // Server config (`vsk_included`) overrides this once /plans loads. Safer
  // than defaulting true and flashing the wrong label during the API
  // round-trip.
  const [vskIncluded, setVskIncluded] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Info fields
  const [kennitala, setKennitala] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [zip, setZip] = useState('');
  const [city, setCity] = useState('');
  const [email, setEmail] = useState('');
  const [looking, setLooking] = useState(false);
  const [lookupSource, setLookupSource] = useState(''); // 'ja.is' | 'rsk_scrape' | 'none' | ''
  const [lookupAttempted, setLookupAttempted] = useState(false);

  // iter370: the page must not scroll behind the modal on iOS Safari, or the
  // modal drifts under the bottom bar while the body scrolls.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);


  // Pre-populate from existing company settings (so a returning user with
  // partial info doesn't have to retype everything)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [planRes, settingsRes] = await Promise.all([
          billingKontoAPI.plansCatalog(),
          companyAPI.getSettings(),
        ]);
        if (cancelled) return;
        setPlans(planRes.data?.plans || []);
        // Explicit boolean conversion — only render "m. VSK" when the
        // server affirmatively says vsk_included=true.
        setVskIncluded(planRes.data?.vsk_included === true);
        const s = settingsRes.data || {};
        setKennitala(s.kennitala || '');
        setName(s.company_name || '');
        setAddress(s.address || '');
        setZip(s.zip || '');
        setCity(s.city || '');
        setEmail(s.email || authUser?.email || '');
      } catch (e) {
        // non-fatal — user can still fill manually
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (open) {
      setError('');
      if (initialPlan) setSelectedPlan(initialPlan);
    }
  }, [open, mode, initialPlan]);

  // Prefill the plan: whatever the company is already on, else the cheapest.
  useEffect(() => {
    if (!open || mode === 'info-only' || selectedPlan) return;
    const current = planCtx?.subscription?.plan;
    if (current && plans.some((p) => p.plan === current)) setSelectedPlan(current);
    else if (plans.length) setSelectedPlan(plans[0].plan);
  }, [open, mode, plans, selectedPlan, planCtx?.subscription?.plan]);

  const ktClean = useMemo(() => onlyDigits(kennitala), [kennitala]);
  // iter359: the real checksum, same rules as the server, so a typo is caught
  // here instead of days later when Konto refuses the customer.
  const ktError = useMemo(
    () => (ktClean.length ? companyKennitalaError(ktClean, name, lang) : ''),
    [ktClean, name, lang],
  );
  const ktValid = ktClean.length === 10 && !ktError;
  const allFieldsPresent =
    ktValid && name.trim() && address.trim() && zip.trim() && city.trim();

  const handleLookup = async () => {
    setError('');
    setLookupSource('');
    if (ktClean.length !== 10 || ktError) {
      setError(ktError || t.invalidKt);
      return;
    }
    setLooking(true);
    setLookupAttempted(true);
    try {
      const res = await companyAPI.prefillFromKennitala(ktClean);
      const d = res.data || {};
      if (d.ok && d.fields) {
        // User confirms — pre-fill empty fields, never overwrite what they typed
        setName((prev) => prev.trim() || d.fields.name || '');
        setAddress((prev) => prev.trim() || d.fields.address || '');
        setZip((prev) => prev.trim() || d.fields.zip || '');
        setCity((prev) => prev.trim() || d.fields.city || '');
        setLookupSource(d.source || 'unknown');
      } else {
        setLookupSource('none');
      }
    } catch (e) {
      setLookupSource('none');
    } finally {
      setLooking(false);
    }
  };

  const saveCompanyInfo = async () => {
    await companyAPI.updateSettings({
      company_name: name.trim(),
      kennitala: ktClean,
      address: address.trim(),
      zip: zip.trim(),
      city: city.trim(),
      email: email.trim(),
    });
  };

  /**
   * iter359: ONE button. Saves the company info and picks the pakki in a
   * single tap. Every failure says what happened, in Icelandic, and the modal
   * stays open so the user can fix it — never a silent close.
   */
  const handleSubmit = async () => {
    setError('');
    if (ktError) {
      setError(ktError);
      return;
    }
    if (!allFieldsPresent) {
      setError(t.errorMissingFields);
      return;
    }
    if (email && !email.includes('@')) {
      setError(t.errorInvalidEmail);
      return;
    }
    if (mode !== 'info-only' && !selectedPlan) {
      setError(lang === 'is' ? 'Veldu pakka fyrst.' : 'Pick a plan first.');
      return;
    }
    setBusy(true);
    try {
      await saveCompanyInfo();
      if (mode === 'info-only') {
        try { await planCtx?.refresh?.(); } catch {}
        onSuccess?.({ source: 'info-only' });
        return;
      }
      await handleActivate();
    } catch (e) {
      setError(e?.response?.data?.detail
        || (lang === 'is' ? 'Vistun mistókst, reyndu aftur.' : 'Save failed, try again.'));
      setBusy(false);
      return;
    }
    setBusy(false);
  };

  const handleActivate = async () => {
    setError('');
    try {
      const res = await billingKontoAPI.selectPlan(selectedPlan);
      try { await planCtx?.refresh?.(); } catch {}
      // Surface activation outcome so the user knows whether Konto accepted
      // the data and the invoice is on its way. Without this the modal
      // closes silently even if Konto rejected the kennitala (the bug we
      // hit in production).
      const activation = res?.data?.activation || {};
      onSuccess?.({
        source: 'full',
        plan: selectedPlan,
        activation: activation.status,
        activationError: activation.error || null,
      });
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const code = e?.response?.data?.detail_code
        || (raw && typeof raw === 'object' ? raw.code : raw)
        || 'activation_failed';
      // Map the soft-confirmation 403 to a friendly localised message
      if (code === 'email_verification_required') {
        setError(lang === 'is'
          ? 'Staðfestu netfangið þitt fyrst (sjáðu borðann efst) til að virkja áskrift.'
          : 'Confirm your email first (banner at top) to activate the subscription.');
      } else {
        // Prefer the server sentence (iter367 sends the whole explanation),
        // then the already-localized `.detail` from the api.js interceptor.
        setError((raw && typeof raw === 'object' ? raw.message : raw)
          || (lang === 'is'
            ? 'Pakkinn var ekki valinn. Reyndu aftur eða hafðu samband.'
            : 'The plan was not selected. Try again or contact us.'));
      }
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal-overlay z-[100] flex items-center justify-center bg-background/85 backdrop-blur-sm p-4"
      data-testid="paywall-flow"
      onClick={(e) => {
        // Backdrop click to dismiss — only if user hasn't started typing yet
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div
        className="modal-shell relative w-full max-w-md rounded-2xl bg-background border border-border shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
          <div>
            <h2 className="text-lg font-semibold tracking-tight" data-testid="paywall-title">
              {mode === 'info-only' ? t.titleInfo : t.titleFull}
            </h2>
            <p className="text-xs text-foreground-muted mt-0.5">
              {mode === 'info-only' ? t.subtitleInfo : t.subtitleFull}
            </p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-card transition-colors"
              data-testid="paywall-close"
              aria-label={t.close}
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="modal-body px-5 pb-5">
          {mode !== 'info-only' && (
            <div className="space-y-3 mb-5" data-testid="paywall-plan-step">
              {plans.map((p) => {
                const Icon = PLAN_ICONS[p.plan] || Sparkles;
                const isSelected = selectedPlan === p.plan;
                return (
                  <button
                    key={p.plan}
                    onClick={() => setSelectedPlan(p.plan)}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isSelected
                        ? 'border-accent bg-accent/5 ring-2 ring-accent/30'
                        : 'border-border hover:border-accent/40 bg-card'
                    }`}
                    data-testid={`paywall-plan-${p.plan}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center ${
                        isSelected ? 'bg-accent text-background' : 'bg-background text-foreground-muted'
                      }`}>
                        <Icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{p.name}</span>
                          {isSelected && (
                            <span className="text-xs text-accent flex items-center gap-1">
                              <Check size={12} /> {t.selected}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-foreground-muted mt-0.5" data-testid={`paywall-plan-tagline-${p.plan}`}>
                          {planTagline(p.plan, p.max_users, lang)}
                        </p>
                        <p className="mt-2 text-2xl font-bold tracking-tight">
                          {formatISK(p.monthly_isk_inc_vat)}
                          <span className="text-sm font-normal text-foreground-muted ml-1">
                            {t.perMonth}
                          </span>
                        </p>
                        <p className="text-xs text-foreground-muted">{vskIncluded ? t.inclVat : t.noVat}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="space-y-4" data-testid="paywall-info-step">
              {/* Kennitala block with lookup */}
              <div>
                <label className="text-xs text-foreground-muted mb-1 block flex items-center gap-1.5">
                  <Hash size={12} /> {t.kennitalaLabel}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={kennitala}
                    onChange={(e) => { setKennitala(e.target.value); setLookupSource(''); }}
                    className="input flex-1 text-sm"
                    placeholder="1234567890"
                    maxLength={11}
                    inputMode="numeric"
                    data-testid="paywall-kennitala-input"
                  />
                  <button
                    onClick={handleLookup}
                    disabled={!ktValid || looking}
                    className="btn btn-secondary text-sm whitespace-nowrap"
                    data-testid="paywall-lookup-btn"
                  >
                    {looking
                      ? <Loader2 size={14} className="animate-spin" />
                      : <>{lookupAttempted ? t.lookupAgainBtn : t.lookupBtn}</>}
                  </button>
                </div>
                <p className="text-xs text-foreground-muted mt-1">{t.kennitalaHelp}</p>
                {ktError && (
                  <p className="text-xs text-error mt-1.5 flex items-start gap-1" data-testid="paywall-kennitala-error">
                    <AlertCircle size={12} className="mt-0.5 shrink-0" /> {ktError}
                  </p>
                )}
                {/* Lookup result badge */}
                {lookupSource === 'ja.is' && (
                  <p className="text-xs text-accent mt-2 flex items-center gap-1" data-testid="paywall-lookup-source-ja">
                    <ShieldCheck size={12} /> {t.foundFromJa}
                  </p>
                )}
                {lookupSource === 'rsk_scrape' && (
                  <p className="text-xs text-accent mt-2 flex items-center gap-1" data-testid="paywall-lookup-source-rsk">
                    <ShieldCheck size={12} /> {t.foundFromRsk}
                  </p>
                )}
                {lookupSource === 'none' && lookupAttempted && (
                  <p className="text-xs text-amber-400 mt-2 flex items-center gap-1" data-testid="paywall-lookup-not-found">
                    <AlertCircle size={12} /> {t.notFound}
                  </p>
                )}
              </div>

              {/* Confirmable / editable fields */}
              <div>
                <label className="text-xs text-foreground-muted mb-1 block flex items-center gap-1.5">
                  <Building2 size={12} /> {t.nameLabel}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input text-sm w-full"
                  placeholder="Acme ehf."
                  data-testid="paywall-name-input"
                />
              </div>
              <div>
                <label className="text-xs text-foreground-muted mb-1 block flex items-center gap-1.5">
                  <MapPin size={12} /> {t.addressLabel}
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="input text-sm w-full"
                  placeholder="Hverfisgata 42"
                  data-testid="paywall-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-foreground-muted mb-1 block">{t.zipLabel}</label>
                  <input
                    type="text"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    className="input text-sm w-full"
                    placeholder="101"
                    maxLength={5}
                    inputMode="numeric"
                    data-testid="paywall-zip-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-foreground-muted mb-1 block">{t.cityLabel}</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="input text-sm w-full"
                    placeholder="Reykjavík"
                    data-testid="paywall-city-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-foreground-muted mb-1 block">{t.emailLabel}</label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input text-sm w-full pr-9"
                    placeholder="reikningar@fyrirtaeki.is"
                    data-testid="paywall-email-input"
                  />
                  {email && (
                    <button
                      type="button"
                      onClick={() => setEmail('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-foreground-muted hover:text-foreground hover:bg-card transition-colors"
                      aria-label={lang === 'is' ? 'Hreinsa netfang' : 'Clear email'}
                      data-testid="paywall-email-clear"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
          </div>

          {mode !== 'info-only' && (
            <div className="mt-5 space-y-2" data-testid="paywall-confirm-step">
              <div className="card p-4 space-y-2 border-accent/30 bg-accent/5">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-foreground-muted uppercase tracking-wide">{t.confirmAmount}</span>
                  <span className="text-2xl font-bold tracking-tight" data-testid="paywall-confirm-amount">
                    {formatISK(plans.find((p) => p.plan === selectedPlan)?.monthly_isk_inc_vat || 0)}
                    <span className="text-sm font-normal text-foreground-muted ml-1">{t.perMonth}</span>
                  </span>
                </div>
              </div>
              <p className="text-xs text-foreground-muted" data-testid="paywall-first-invoice-line">
                {(planCtx?.subscription?.reopened_at
                  && reopenNotice(
                    planCtx?.subscription,
                    plans.find((p) => p.plan === selectedPlan)?.monthly_isk_inc_vat || 0,
                    lang))
                  || firstInvoiceLine(planCtx?.subscription, lang)}
              </p>
              <p className="text-xs text-foreground-muted">{t.confirmNote}</p>
            </div>
          )}

          {error && (
            <p className="mt-3 text-sm text-error flex items-center gap-1.5" data-testid="paywall-error">
              <AlertCircle size={14} /> {error}
            </p>
          )}
        </div>

        {/* Footer — iter359: ONE button, no stepper, no Áfram.
            iter370: sticky, and it clears Safari's bottom bar. */}
        <div className="modal-footer border-t border-border px-5 pt-3.5" data-testid="paywall-footer">
          {/* iter359: never a dead grey button with no explanation. */}
          {!allFieldsPresent && (
            <p className="text-xs text-amber-300 mb-2 flex items-start gap-1.5" data-testid="paywall-blocked-hint">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              {ktError || (lang === 'is'
                ? `Fylltu út: ${[
                  !ktClean && t.kennitalaLabel,
                  !name.trim() && t.nameLabel,
                  !address.trim() && t.addressLabel,
                  !zip.trim() && t.zipLabel,
                  !city.trim() && t.cityLabel,
                ].filter(Boolean).join(', ')}`
                : `Fill in: ${[
                  !ktClean && t.kennitalaLabel,
                  !name.trim() && t.nameLabel,
                  !address.trim() && t.addressLabel,
                  !zip.trim() && t.zipLabel,
                  !city.trim() && t.cityLabel,
                ].filter(Boolean).join(', ')}`)}
            </p>
          )}
          <button
            onClick={handleSubmit}
            disabled={busy || !allFieldsPresent || (mode !== 'info-only' && !selectedPlan)}
            className="btn btn-primary text-sm w-full justify-center"
            data-testid="paywall-activate-btn"
          >
            {busy
              ? <>{t.activating} <Loader2 size={14} className="animate-spin" /></>
              : (mode === 'info-only'
                ? <>{t.saveAndContinue} <Check size={14} /></>
                : <>{t.activate} <Check size={14} /></>)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaywallFlow;
